import { ClaimRewardsButton } from 'components/ClaimRewardsButton'
import CurrencyLogo from 'components/CurrencyLogo'
import { ManageButtonFlex } from 'components/PositionListItem/styled'
import { ROUTES } from 'config/routes'
import { XKdkStakingPosition } from 'hooks/portfolio/useXKdkStakingPosition'
import { StyledInternalLink } from 'theme'
import { formatNumber } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'

import { PortfolioConnectorLines } from './PortfolioConnectorLines'
import { PortfolioManageButton } from './PortfolioRowPrimitives'
import { PortfolioSchemaRow } from './PortfolioSchemaRow'
import { TokenBalanceDisplay } from './TokenBalanceDisplay'

interface StakingRowProps {
  position: XKdkStakingPosition
  onClaimSuccess?: () => void
}

export function StakingRow({ position, onClaimSuccess }: StakingRowProps) {
  const { xkdkToken, xkdkBalance, xkdkBalanceUSD, currentApr, totalValueUSD, pendingRewards } = position

  const allocatedAmount = xkdkBalance.allocated ? parseFloat(xkdkBalance.allocated.toExact()) : 0
  const redeemingAmount = xkdkBalance.redeeming ? parseFloat(xkdkBalance.redeeming.toExact()) : 0

  const manageRoute = ROUTES.staking

  return (
    <PortfolioSchemaRow
      hasExpandableContent
      mobileClassName="p-3 space-y-2"
      desktopClassName="grid-cols-6 items-center py-3 px-6 gap-4"
      identity={{
        leading: <CurrencyLogo currency={xkdkToken} symbol="xKDK" size="32px" />,
        title: 'xKDK',
        subtitle: 'Kodiak Escrowed Token',
        mobileTitle: 'xKDK Staking',
        mobileSubtitle: 'xKDK',
      }}
      mobile={{
        value: (
          <div className="text-kodiak-text-primary font-semibold text-base">{formatDollarAmount(totalValueUSD)}</div>
        ),
        rootClassName: 'flex items-center justify-between',
        leftClassName: 'flex items-center space-x-3',
        leftTextClassName: '',
        titleClassName: 'font-semibold text-kodiak-text-primary text-base',
        subtitleClassName: 'text-xs text-kodiak-text-secondary',
        valueRowClassName: 'flex items-center space-x-2',
      }}
      desktop={{
        showExpandIcon: true,
        rowClassName: 'flex items-center space-x-3',
        textClassName: '',
        titleClassName: 'font-semibold text-kodiak-text-primary text-base md:whitespace-nowrap',
        subtitleClassName: 'text-xs text-kodiak-text-secondary',
        columns: (
          <>
            <div className="text-right space-y-0.5">
              <div className="text-xs flex items-center justify-end gap-1">
                <span className="text-kodiak-text-secondary">Staked:</span>
                <span className="text-kodiak-text-primary">{formatNumber(allocatedAmount)} xKDK</span>
                <span className="text-kodiak-text-secondary">({formatDollarAmount(xkdkBalanceUSD.allocated)})</span>
              </div>
              {redeemingAmount > 0 && (
                <div className="text-xs flex items-center justify-end gap-1">
                  <span className="text-kodiak-text-secondary">Redeeming:</span>
                  <span className="text-kodiak-text-primary">{formatNumber(redeemingAmount)} xKDK</span>
                  <span className="text-kodiak-text-secondary">({formatDollarAmount(xkdkBalanceUSD.redeeming)})</span>
                </div>
              )}
            </div>

            <div className="text-right">
              <div className="text-sm text-kodiak-text-primary">
                {currentApr !== undefined ? `${currentApr.toFixed(2)}%` : '—'}
              </div>
            </div>

            <div className="text-kodiak-text-primary font-semibold text-right text-base">
              {formatDollarAmount(totalValueUSD)}
            </div>

            <div className="text-right">
              {pendingRewards.length > 0 ? (
                <div className="text-sm text-[#6EE7B7] space-y-1">
                  {pendingRewards.map((reward, index) => (
                    <div key={index}>
                      <span>
                        {formatNumber(parseFloat(reward.amount.toExact()))} {reward.token.symbol}
                      </span>{' '}
                      <span className="text-[#6EE7B7]">({formatDollarAmount(reward.amountUSD)})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-kodiak-text-secondary">—</div>
              )}
            </div>

            <div className="flex flex-col items-end justify-end gap-2">
              <PortfolioManageButton
                as={StyledInternalLink}
                to={manageRoute}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                Manage
              </PortfolioManageButton>
            </div>
          </>
        ),
      }}
      renderExpanded={() => (
        <div className="bg-kodiak-bg-module relative">
          {/* Mobile Layout */}
          <div className="sm:hidden bg-[#222f42]">
            {/* Staked sub-row */}
            <div className="relative">
              <PortfolioConnectorLines
                verticalLeftClassName="left-3"
                verticalTop="8px"
                verticalBottom={redeemingAmount > 0 ? '0' : '8px'}
                zIndex={10}
                showDesktop={false}
              />

              <div className="pl-6 pr-3 py-2 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="text-kodiak-text-secondary text-xs">Staked</div>
                  <div className="text-right">
                    <div className="text-kodiak-text-primary text-xs">{formatNumber(allocatedAmount)} xKDK</div>
                    <div className="text-kodiak-text-secondary text-xs">
                      {formatDollarAmount(xkdkBalanceUSD.allocated)}
                    </div>
                  </div>
                </div>

                {currentApr !== undefined && (
                  <div className="flex items-center justify-between">
                    <div className="text-kodiak-text-secondary text-xs">APR</div>
                    <div className="text-kodiak-text-primary text-xs">{currentApr.toFixed(2)}%</div>
                  </div>
                )}

                {pendingRewards.length > 0 && (
                  <div className="flex items-center justify-between">
                    <div className="text-kodiak-text-secondary text-xs">Protocol Rewards</div>
                    <div className="text-right">
                      {pendingRewards.map((reward, index) => (
                        <div key={index} className="text-[#6EE7B7] text-xs">
                          <span>
                            {formatNumber(parseFloat(reward.amount.toExact()))} {reward.token.symbol}
                          </span>{' '}
                          <span className="text-[#6EE7B7]">({formatDollarAmount(reward.amountUSD)})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <ClaimRewardsButton
                    pendingRewards={pendingRewards}
                    onClaimSuccess={onClaimSuccess}
                    showTooltip={false}
                  />
                </div>
              </div>
            </div>

            {/* Redeeming sub-row */}
            {redeemingAmount > 0 && (
              <div className="relative">
                <PortfolioConnectorLines
                  verticalLeftClassName="left-3"
                  verticalTop="0"
                  verticalBottom="8px"
                  zIndex={10}
                  showDesktop={false}
                />

                <div className="pl-6 pr-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="text-kodiak-text-secondary text-xs">Redeeming</div>
                    <div className="text-right">
                      <div className="text-kodiak-text-primary text-xs">{formatNumber(redeemingAmount)} xKDK</div>
                      <div className="text-kodiak-text-secondary text-xs">
                        {formatDollarAmount(xkdkBalanceUSD.redeeming)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <ManageButtonFlex className="py-2 px-3">
              <div className="flex flex-col items-end gap-2 w-full">
                <PortfolioManageButton
                  as={StyledInternalLink}
                  to={manageRoute}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  style={{ width: '100%' }}
                >
                  Manage
                </PortfolioManageButton>
              </div>
            </ManageButtonFlex>
          </div>

          {/* Desktop Layout */}
          <div className="hidden sm:block">
            {/* Staked sub-row */}
            <div className="relative">
              <PortfolioConnectorLines
                verticalLeftClassName="left-6"
                verticalTop="8px"
                verticalBottom={redeemingAmount > 0 ? '0' : '8px'}
                zIndex={10}
                showMobile={false}
              />

              <div className="grid grid-cols-6 items-center gap-4 py-2 pl-10 pr-8 bg-[#222f42]">
                {/* Label */}
                <div className="text-xs text-kodiak-text-secondary">Staked</div>

                {/* Balance */}
                <TokenBalanceDisplay amount={allocatedAmount} symbol="xKDK" usdValue={xkdkBalanceUSD.allocated} />

                {/* APR */}
                <div className="text-right">
                  <div className="text-xs text-kodiak-text-primary">
                    {currentApr !== undefined ? `${currentApr.toFixed(2)}%` : '—'}
                  </div>
                </div>

                {/* Total Value */}
                <div className="text-xs text-kodiak-text-primary text-right">
                  {formatDollarAmount(xkdkBalanceUSD.allocated)}
                </div>

                {/* Protocol Rewards */}
                <div className="text-right">
                  {pendingRewards.length > 0 ? (
                    <div className="text-xs text-[#6EE7B7] space-y-1">
                      {pendingRewards.map((reward, index) => (
                        <div key={index}>
                          <span>
                            {formatNumber(parseFloat(reward.amount.toExact()))} {reward.token.symbol}
                          </span>{' '}
                          <span className="text-[#6EE7B7]">({formatDollarAmount(reward.amountUSD)})</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-kodiak-text-secondary">—</div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <ClaimRewardsButton pendingRewards={pendingRewards} onClaimSuccess={onClaimSuccess} showTooltip />
                </div>
              </div>
            </div>

            {/* Redeeming sub-row */}
            {redeemingAmount > 0 && (
              <div className="relative">
                <PortfolioConnectorLines
                  verticalLeftClassName="left-6"
                  verticalTop="0"
                  verticalBottom="8px"
                  zIndex={10}
                  showMobile={false}
                />

                <div className="grid grid-cols-6 items-center gap-4 py-2 pl-10 pr-8 bg-[#222f42]">
                  {/* Label */}
                  <div className="text-xs text-kodiak-text-secondary">Redeeming</div>

                  {/* Balance */}
                  <TokenBalanceDisplay amount={redeemingAmount} symbol="xKDK" usdValue={xkdkBalanceUSD.redeeming} />

                  {/* APR - empty for redeeming */}
                  <div className="text-right">
                    <div className="text-xs text-kodiak-text-secondary">—</div>
                  </div>

                  {/* Total Value */}
                  <div className="text-xs text-kodiak-text-primary text-right">
                    {formatDollarAmount(xkdkBalanceUSD.redeeming)}
                  </div>

                  {/* Protocol Rewards - empty for sub-row */}
                  <div className="text-xs text-kodiak-text-secondary text-right">—</div>

                  {/* Empty for alignment */}
                  <div></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    />
  )
}
